import type { RouteLocationRaw } from "vue-router";

const MARKETING_NAV_OFFSET = 72;

/** Section ids on the marketing homepage (/) — not valid on /contact etc. */
export const MARKETING_HOME_SECTIONS = new Set([
  "about",
  "approach",
  "problem",
  "what",
  "work",
]);

function normalizeMarketingPath(path: string): string {
  const base = path.split("#")[0]?.replace(/\/$/, "") || "/";
  return base || "/";
}

/** Parse `#id` or `/#id` into a section id, or null if not a hash link. */
export function parseMarketingSectionId(href: string): string | null {
  const trimmed = href.trim();
  if (trimmed.startsWith("/#")) {
    const id = trimmed.slice(2);
    return id || null;
  }
  if (trimmed.startsWith("#")) {
    const id = trimmed.slice(1);
    return id || null;
  }
  return null;
}

/**
 * Homepage section links. Off the homepage use the string `/#section` so Ionic /
 * NuxtLink do not stick the hash on the current path (e.g. `/contact#approach`).
 */
export function resolveMarketingSectionTo(
  href: string,
  currentPath?: string,
): RouteLocationRaw {
  const trimmed = href.trim();
  const id = parseMarketingSectionId(trimmed);

  if (!id) return trimmed;

  const onHome = normalizeMarketingPath(currentPath ?? "/") === "/";

  if (onHome) return `#${id}`;
  return `/#${id}`;
}

/** @deprecated Use resolveMarketingSectionTo */
export function resolveMarketingSectionHref(
  href: string,
  currentPath?: string,
): string {
  const to = resolveMarketingSectionTo(href, currentPath);
  if (typeof to === "string") return to;
  if (to.path && to.hash) return `${to.path}${to.hash}`;
  if (to.hash) return to.hash;
  return to.path ?? "/";
}

export function isMarketingSectionHref(href: string): boolean {
  const id = parseMarketingSectionId(href);
  return id !== null && MARKETING_HOME_SECTIONS.has(id);
}

export function isMarketingInternalLink(to: RouteLocationRaw): boolean {
  if (typeof to === "string") {
    return (
      (to.startsWith("/") || to.startsWith("#")) &&
      !to.startsWith("//") &&
      !to.includes(":")
    );
  }
  return true;
}

/** Scroll a homepage section into view inside ion-content (not window). */
export async function scrollMarketingToHash(
  hash: string,
  offset = MARKETING_NAV_OFFSET,
) {
  const id = hash.replace(/^#/, "");
  if (!id || !import.meta.client) return;

  const scrollRoot = document.querySelector(
    ".marketing-content",
  ) as HTMLElement | null;

  for (let attempt = 0; attempt < 8; attempt++) {
    await nextTick();
    await new Promise((r) => setTimeout(r, 80 + attempt * 60));

    const target = document.getElementById(id);
    if (!target || !scrollRoot) continue;

    const rect = target.getBoundingClientRect();
    const scrollRect = scrollRoot.getBoundingClientRect();
    const top = scrollRoot.scrollTop + rect.top - scrollRect.top - offset;
    scrollRoot.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    return;
  }
}

/** Navigate to a homepage section from any marketing page. */
export async function navigateToMarketingSection(
  href: string,
  currentPath?: string,
) {
  const to = resolveMarketingSectionTo(href, currentPath);
  const path = normalizeMarketingPath(currentPath ?? useRoute().path);

  if (typeof to === "string" && to.startsWith("/#")) {
    await navigateTo(to);
    await scrollMarketingToHash(to.slice(1));
    return;
  }

  if (typeof to === "string" && to.startsWith("#") && path === "/") {
    if (useRoute().hash !== to) {
      await navigateTo({ hash: to });
    }
    await scrollMarketingToHash(to);
    return;
  }

  await navigateTo(to);
}

/**
 * `/contact#approach` → `/#approach`. Homepage sections only exist on `/`.
 */
export function useMarketingOffHomeHashRedirect() {
  const route = useRoute();

  const run = () => {
    if (!import.meta.client) return;

    const path = normalizeMarketingPath(route.path);
    if (path === "/") return;

    const id = route.hash.replace(/^#/, "");
    if (!id || !MARKETING_HOME_SECTIONS.has(id)) return;

    void navigateTo(`/#${id}`, { replace: true });
  };

  watch(() => route.fullPath, run);
  onMounted(run);
}

export function useMarketingHashScroll(
  contentRef?: Ref<HTMLElement | null>,
) {
  const route = useRoute();

  const syncFromRoute = () => {
    if (route.path !== "/" || !route.hash) return;
    void scrollMarketingToHash(route.hash);
  };

  watch(() => route.hash, syncFromRoute);

  onMounted(() => {
    syncFromRoute();
  });

  if (contentRef) {
    watch(contentRef, () => {
      if (contentRef.value) syncFromRoute();
    });
  }

  return { scrollMarketingToHash, syncFromRoute };
}
